import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import { withoutAuth } from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';
import Cookies from 'js-cookie';

import Logo from "../../assets/img/favicon.png"

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  const login = async () => {
    setLoading(true);
    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_URI + 'user/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Store tokens in cookies and localStorage
        Cookies.set('accessToken', data.token);
        localStorage.setItem('refreshToken', data.refreshToken); // Store refresh token
        
        if(data.extraToken != null) {
          Cookies.set('adminKey', data.extraToken);
        }
        
        // Store user data in localStorage
        const userData = {
          username: data.username,
          accessToken: data.token,
          refreshToken: data.refreshToken
        };
        localStorage.setItem('user', JSON.stringify(userData));
        
        setAlertMessage('Login successful.');
        setAlertType('success');
        setAlertOpen(true);
        
        // Animate before redirect
        setTimeout(() => {
          window.location.href = '/';
        }, 800);
      } else {
        setAlertMessage(data.message);
        setAlertType('error');
        setAlertOpen(true);
      }
    } catch (error) {
      setAlertMessage('Si è verificato un errore. Riprova più tardi.');
      setAlertType('error');
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  }
  
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        setAlertOpen(false);
      }, 10000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [alertOpen]);

  return (
    <section className='min-h-[100vh] bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center'>
      <motion.div 
        className="w-full max-w-md p-8 md:p-10 bg-white rounded-2xl shadow-xl"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col items-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Link to={"/"}>
              <img
                alt="Marrocco ART"
                src={Logo}
                className="h-20 w-auto"
              />
            </Link>
          </motion.div>
          <motion.h2 
            className="mt-6 text-center text-2xl font-bold text-gray-900"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Bentornato
          </motion.h2>
          <motion.p
            className="mt-2 text-center text-sm text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Accedi al tuo account
          </motion.p>
        </div>

        <motion.form
          onSubmit={(event) => {
            event.preventDefault();
            login();
          }}
          className="mt-8 space-y-6"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          custom={1}
        >
          <motion.div variants={fadeIn} custom={2}>
            <div className="flex items-center space-x-2 mb-2">
              <FiMail className="text-gray-500" />
              <label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </label>
            </div>
            <div className="relative">
              <input 
                className="w-full px-4 py-3 text-gray-800 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200"
                type="email" 
                placeholder="Il tuo indirizzo email"
                value={email}
                onChange={handleEmailChange}
                required
              />
            </div>
          </motion.div>

          <motion.div variants={fadeIn} custom={3}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <FiLock className="text-gray-500" />
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </label>
              </div>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <a 
                  href="#" 
                  className="text-xs text-[#08a5ec] hover:text-[#126eeb] transition-colors duration-200"
                >
                  Password dimenticata?
                </a>
              </motion.div>
            </div>
            <div className="relative">
              <input
                className="w-full px-4 py-3 text-gray-800 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200"
                type="password" 
                placeholder="La tua password"
                value={password}
                onChange={handlePasswordChange}
                required
              />
            </div>
          </motion.div>

          <motion.div variants={fadeIn} custom={4}>
            <motion.button
              type="submit"
              className="relative w-full flex items-center justify-center px-6 py-3 text-white bg-gradient-to-r from-[#08a5ec] to-[#126eeb] rounded-lg font-medium shadow-md"
              variants={buttonVariants}
              initial="idle"
              whileHover="hover"
              whileTap="tap"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Accesso in corso...
                </span>
              ) : (
                <span className="flex items-center">
                  <FiLogIn className="mr-2" />
                  Accedi
                </span>
              )}
            </motion.button>
          </motion.div>
        </motion.form>

        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <p className="text-sm text-gray-600">
            Non hai un account?{' '}
            <Link to="/register" className="font-medium text-[#08a5ec] hover:text-[#126eeb] transition-colors duration-200">
              Registrati ora
            </Link>
          </p>
        </motion.div>
      </motion.div>

      <motion.div 
        className="fixed top-8 right-8 z-50"
        initial={{ opacity: 0, y: -20 }}
        animate={{ 
          opacity: alertOpen ? 1 : 0,
          y: alertOpen ? 0 : -20,
          pointerEvents: alertOpen ? 'auto' : 'none'
        }}
        transition={{ duration: 0.3 }}
      >
        {alertOpen && (
          <Alert 
            severity={alertType} 
            onClose={() => setAlertOpen(false)}
            className="shadow-lg border border-white/10"
            sx={{
              backgroundColor: alertType === 'success' ? '#10B981' : '#EF4444',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            {alertMessage}
          </Alert>
        )}
      </motion.div>
    </section>
  );
}

export default withoutAuth(Login);